import {useMutation} from '@apollo/react-hooks';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import type {
    MakeAudiencePrivate as MakeAudiencePrivateMutation,
    MakeAudiencePrivateVariables as MakeAudiencePrivateVariables,
    GetAudiencesForPreferenceCenter as GetAudiencesForPreferenceCenterQuery,
} from 'nutshell-graphql-types';

import MAKE_AUDIENCE_PRIVATE from '../mutations/make-audience-private.graphql';
import GET_AUDIENCES from '../queries/get-audiences.graphql';

export const useMakeAudiencePrivate = (): {
    makeAudiencePrivate: (arg1: string) => Promise<any>;
    isLoading: boolean;
    error: string | null | undefined;
} => {
    const [makeAudiencePrivate, {loading, error}] = useMutation<
        MakeAudiencePrivateMutation,
        MakeAudiencePrivateVariables
    >(MAKE_AUDIENCE_PRIVATE);

    return {
        makeAudiencePrivate: (audienceId: string) =>
            makeAudiencePrivate({
                variables: {audienceId},
                update: (proxy, mutationResults) => {
                    const cacheResult: GetAudiencesForPreferenceCenterQuery | null | undefined =
                        proxy.readQuery({
                            query: GET_AUDIENCES,
                            variables: {
                                first: 10,
                                sort: 'NAME_ASC',
                                audienceTypeFilter: 'ONLY_PUBLIC',
                            },
                        });

                    const audience =
                        mutationResults &&
                        mutationResults.data &&
                        mutationResults.data.audienceUpdate &&
                        mutationResults.data.audienceUpdate.audience;

                    if (!audience) return;

                    if (
                        cacheResult &&
                        cacheResult.audiences.edges.find((edge) => edge.node.id === audience.id)
                    ) {
                        const data = {
                            audiences: {
                                ...cacheResult.audiences,
                                edges: cacheResult.audiences.edges.filter(
                                    (edge) => edge.node.id !== audience.id
                                ),
                                totalCount: cacheResult.audiences.totalCount - 1,
                            },
                        } as const;

                        proxy.writeQuery({
                            query: GET_AUDIENCES,
                            variables: {
                                first: 10,
                                sort: 'NAME_ASC',
                                audienceTypeFilter: 'ONLY_PUBLIC',
                            },
                            data,
                        });
                    }
                },
            }),
        isLoading: loading,
        // @ts-expect-error
        error: safelyGetGraphQLErrorMessage(error),
    };
};
